import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Chip,
  CircularProgress,
  IconButton,
} from '@mui/material'
import { X, Sparkles, Plus, Trash2 } from 'lucide-react'
import AnimatedLoadingText from 'components/AnimatedLoadingText/AnimatedLoadingText'
import {
  Platform,
  useGenerateTopicKeywordsLazyQuery,
  useCreateListeningTopicMutation,
} from 'graphql/generated/graphql'

const PLATFORMS: Array<{ value: Platform; label: string; emoji: string }> = [
  { value: Platform.Instagram, label: 'Instagram', emoji: '📷' },
  { value: Platform.TikTok, label: 'TikTok', emoji: '🎵' },
  { value: Platform.Youtube, label: 'YouTube', emoji: '🎥' },
  { value: Platform.Reddit, label: 'Reddit', emoji: '🔴' },
  { value: Platform.Twitter, label: 'Twitter', emoji: '🐦' },
  { value: Platform.Facebook, label: 'Facebook', emoji: '📘' },
  { value: Platform.Bluesky, label: 'Bluesky', emoji: '🦋' },
]

interface CreateTopicDialogProps {
  open: boolean
  onClose: () => void
  onCreated: () => void
}

type Step = 'describe' | 'keywords' | 'saving'

export default function CreateTopicDialog({ open, onClose, onCreated }: CreateTopicDialogProps) {
  const [step, setStep] = useState<Step>('describe')
  const [description, setDescription] = useState('')
  const [name, setName] = useState('')
  const [selectedPlatforms, setSelectedPlatforms] = useState<Platform[]>([Platform.Instagram, Platform.TikTok])
  const [minFollowers, setMinFollowers] = useState('')
  const [maxFollowers, setMaxFollowers] = useState('')
  const [keywords, setKeywords] = useState<Record<string, string[]>>({})
  const [newKeyword, setNewKeyword] = useState<Record<string, string>>({})

  const [generateKeywords, { loading: generatingKeywords }] = useGenerateTopicKeywordsLazyQuery()
  const [createTopic, { loading: creating }] = useCreateListeningTopicMutation()
  const [error, setError] = useState<string | null>(null)

  // Podcast-specific templates
  const templates = [
    { id: 'truecrime', iconEmoji: '🔍', displayName: 'True Crime', description: 'True crime stories, cold cases, criminal psychology, investigations' },
    { id: 'interview', iconEmoji: '🎤', displayName: 'Interview Shows', description: 'Interview-style podcasts, guest conversations, Q&A formats' },
    { id: 'business', iconEmoji: '💼', displayName: 'Business & Startups', description: 'Entrepreneurship, startup stories, business strategy, side hustles' },
    { id: 'comedy', iconEmoji: '😂', displayName: 'Comedy', description: 'Comedy podcasts, stand-up, improv, funny stories and commentary' },
    { id: 'health', iconEmoji: '🧘', displayName: 'Health & Wellness', description: 'Mental health, fitness, nutrition, mindfulness, self-improvement' },
    { id: 'tech', iconEmoji: '💻', displayName: 'Tech & AI', description: 'Technology trends, AI, software, gadgets, digital culture' },
    { id: 'politics', iconEmoji: '🏛️', displayName: 'Politics & News', description: 'Political commentary, current events, news analysis' },
    { id: 'sports', iconEmoji: '⚽', displayName: 'Sports', description: 'Sports commentary, analysis, athlete stories, fantasy sports' },
    { id: 'science', iconEmoji: '🔬', displayName: 'Science', description: 'Science education, discoveries, space, nature, research' },
    { id: 'storytelling', iconEmoji: '📖', displayName: 'Storytelling', description: 'Narrative storytelling, fiction, audio dramas, personal stories' },
    { id: 'culture', iconEmoji: '🎬', displayName: 'Pop Culture', description: 'Movies, TV, music, celebrity, entertainment news and reviews' },
    { id: 'education', iconEmoji: '📚', displayName: 'Education', description: 'Learning, teaching, history, language, academic topics' },
    { id: 'parenting', iconEmoji: '👶', displayName: 'Parenting & Family', description: 'Parenting advice, family life, relationships, kids and teens' },
    { id: 'finance', iconEmoji: '💰', displayName: 'Personal Finance', description: 'Investing, budgeting, financial independence, money management' },
    { id: 'gaming', iconEmoji: '🎮', displayName: 'Gaming', description: 'Video games, esports, game reviews, gaming culture' },
    { id: 'spirituality', iconEmoji: '🙏', displayName: 'Spirituality & Faith', description: 'Religion, spirituality, meditation, faith-based content' },
  ]

  const handleSelectTemplate = (template: { displayName: string; description?: string | null; iconEmoji?: string | null }) => {
    setName(template.displayName)
    setDescription(template.description ?? `${template.displayName} podcast topics and conversations`)
  }

  const handleClose = () => {
    setStep('describe')
    setDescription('')
    setName('')
    setSelectedPlatforms([Platform.Instagram, Platform.TikTok])
    setMinFollowers('')
    setMaxFollowers('')
    setKeywords({})
    setNewKeyword({})
    setError(null)
    onClose()
  }

  const togglePlatform = (platform: Platform) => {
    setSelectedPlatforms((prev) =>
      prev.includes(platform) ? prev.filter((p) => p !== platform) : [...prev, platform]
    )
  }

  const handleGenerateKeywords = async () => {
    if (!description.trim() || selectedPlatforms.length === 0) return
    setError(null)

    try {
      const result = await generateKeywords({
        variables: { description, platforms: selectedPlatforms },
      })

      if (result.data?.generateTopicKeywords?.keywords) {
        setKeywords(result.data.generateTopicKeywords.keywords as Record<string, string[]>)
        if (!name.trim()) {
          setName(description.length > 50 ? description.slice(0, 47) + '...' : description)
        }
        setStep('keywords')
      } else {
        setError('Could not generate keywords. Please try again.')
      }
    } catch (err: any) {
      console.error('Keyword generation error:', err)
      setError(err.message ?? 'Failed to generate keywords. Please try again.')
    }
  }

  const handleRemoveKeyword = (platform: string, index: number) => {
    setKeywords((prev) => ({
      ...prev,
      [platform]: prev[platform].filter((_, i) => i !== index),
    }))
  }

  const handleAddKeyword = (platform: string) => {
    const kw = newKeyword[platform]?.trim()
    if (!kw) return
    setKeywords((prev) => ({
      ...prev,
      [platform]: [...(prev[platform] ?? []), kw],
    }))
    setNewKeyword((prev) => ({ ...prev, [platform]: '' }))
  }

  const handleCreate = async () => {
    setStep('saving')
    setError(null)
    try {
      await createTopic({
        variables: {
          input: {
            name: name.trim() || description.slice(0, 50),
            description,
            platforms: selectedPlatforms,
            seedKeywords: keywords,
            minFollowers: minFollowers ? parseInt(minFollowers, 10) : undefined,
            maxFollowers: maxFollowers ? parseInt(maxFollowers, 10) : undefined,
          },
        },
      })
      handleClose()
      onCreated()
    } catch (err: any) {
      console.error('Error creating topic:', err)
      setError(err.message ?? 'Failed to create topic. Please try again.')
      setStep('keywords')
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={window.innerWidth < 600}
      PaperProps={{
        sx: {
          background: 'rgba(15, 23, 42, 0.95)',
          backdropFilter: 'blur(24px)',
          border: { xs: 'none', sm: '1px solid rgba(168, 85, 247, 0.2)' },
          borderRadius: { xs: 0, sm: '16px' },
          color: 'white',
          m: { xs: 0, sm: 2 },
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '16px', sm: '20px' } }}>
          {step === 'describe' ? 'New Radar Signal' : 'Review Keywords'}
        </Typography>
        <IconButton onClick={handleClose} sx={{ color: 'rgba(255,255,255,0.5)' }}>
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Box sx={{ mb: 2, p: 1.5, backgroundColor: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '10px' }}>
            <Typography sx={{ color: '#ef4444', fontSize: '13px' }}>
              {error}
            </Typography>
          </Box>
        )}
        {step === 'describe' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            {/* Quick Start Templates */}
            {templates.length > 0 && !description.trim() && (
              <Box>
                <Typography sx={{ mb: 1, fontSize: '14px', color: 'rgba(255,255,255,0.7)' }}>
                  Popular podcast categories
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, maxHeight: 120, overflow: 'auto' }}>
                  {templates.map((t) => (
                    <Chip
                      key={t.id}
                      label={`${t.iconEmoji ?? ''} ${t.displayName}`}
                      onClick={() => handleSelectTemplate(t)}
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        color: 'rgba(255,255,255,0.8)',
                        fontSize: '12px',
                        '&:hover': {
                          backgroundColor: 'rgba(168, 85, 247, 0.15)',
                          borderColor: 'rgba(168, 85, 247, 0.3)',
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            <Box>
              <Typography sx={{ mb: 1, fontSize: '14px', color: 'rgba(255,255,255,0.7)' }}>
                {description.trim() ? 'What does your podcast cover?' : 'Or describe your podcast topic'}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="e.g. True crime storytelling, cold cases, criminal psychology"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    borderRadius: '12px',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                    '&:hover fieldset': { borderColor: 'rgba(168, 85, 247, 0.4)' },
                    '&.Mui-focused fieldset': { borderColor: '#a855f7' },
                  },
                }}
              />
            </Box>

            <Box>
              <Typography sx={{ mb: 1, fontSize: '14px', color: 'rgba(255,255,255,0.7)' }}>
                Signal name (optional)
              </Typography>
              <TextField
                fullWidth
                placeholder="Short name for this topic"
                value={name}
                onChange={(e) => setName(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    borderRadius: '12px',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                    '&:hover fieldset': { borderColor: 'rgba(168, 85, 247, 0.4)' },
                    '&.Mui-focused fieldset': { borderColor: '#a855f7' },
                  },
                }}
              />
            </Box>

            <Box>
              <Typography sx={{ mb: 1, fontSize: '14px', color: 'rgba(255,255,255,0.7)' }}>
                Platforms to monitor
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {PLATFORMS.map((p) => (
                  <Chip
                    key={p.value}
                    label={`${p.emoji} ${p.label}`}
                    onClick={() => togglePlatform(p.value)}
                    sx={{
                      backgroundColor: selectedPlatforms.includes(p.value)
                        ? 'rgba(168, 85, 247, 0.3)'
                        : 'rgba(255,255,255,0.05)',
                      border: selectedPlatforms.includes(p.value)
                        ? '1px solid rgba(168, 85, 247, 0.5)'
                        : '1px solid rgba(255,255,255,0.1)',
                      color: 'white',
                      '&:hover': { backgroundColor: 'rgba(168, 85, 247, 0.2)' },
                    }}
                  />
                ))}
              </Box>
            </Box>

            <Box>
              <Typography sx={{ mb: 1, fontSize: '14px', color: 'rgba(255,255,255,0.7)' }}>
                Target audience size (optional)
              </Typography>
              <Typography sx={{ mb: 1.5, fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>
                Focus on creators and potential guests in this follower range
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <TextField
                  placeholder="Min"
                  value={minFollowers}
                  onChange={(e) => setMinFollowers(e.target.value.replace(/\D/g, ''))}
                  size="small"
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      borderRadius: '10px',
                      fontSize: '14px',
                      '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                      '&:hover fieldset': { borderColor: 'rgba(168, 85, 247, 0.4)' },
                      '&.Mui-focused fieldset': { borderColor: '#a855f7' },
                    },
                  }}
                />
                <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '13px' }}>to</Typography>
                <TextField
                  placeholder="Max"
                  value={maxFollowers}
                  onChange={(e) => setMaxFollowers(e.target.value.replace(/\D/g, ''))}
                  size="small"
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      borderRadius: '10px',
                      fontSize: '14px',
                      '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                      '&:hover fieldset': { borderColor: 'rgba(168, 85, 247, 0.4)' },
                      '&.Mui-focused fieldset': { borderColor: '#a855f7' },
                    },
                  }}
                />
              </Box>
              {(minFollowers || maxFollowers) && (
                <Typography sx={{ mt: 0.5, fontSize: '11px', color: 'rgba(168,85,247,0.6)' }}>
                  {minFollowers && maxFollowers
                    ? `${Number(minFollowers).toLocaleString()} - ${Number(maxFollowers).toLocaleString()} followers`
                    : minFollowers
                      ? `${Number(minFollowers).toLocaleString()}+ followers`
                      : `Up to ${Number(maxFollowers).toLocaleString()} followers`}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {step === 'keywords' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)' }}>
              These keywords find conversations related to your podcast. You can add or remove them.
            </Typography>
            {selectedPlatforms.map((platform) => {
              const platformInfo = PLATFORMS.find((p) => p.value === platform)
              const platformKeywords = keywords[platform] ?? []
              return (
                <Box key={platform}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 0.5 }}>
                    {platformInfo?.emoji} {platformInfo?.label}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                    {platformKeywords.map((kw, i) => (
                      <Chip
                        key={i}
                        label={kw}
                        size="small"
                        onDelete={() => handleRemoveKeyword(platform, i)}
                        deleteIcon={<Trash2 size={12} color="rgba(255,255,255,0.5)" />}
                        sx={{
                          backgroundColor: 'rgba(255,255,255,0.08)',
                          color: 'white',
                          border: '1px solid rgba(255,255,255,0.1)',
                          '& .MuiChip-deleteIcon:hover': { color: '#ef4444' },
                        }}
                      />
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      size="small"
                      placeholder="Add keyword..."
                      value={newKeyword[platform] ?? ''}
                      onChange={(e) => setNewKeyword((prev) => ({ ...prev, [platform]: e.target.value }))}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddKeyword(platform)}
                      sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                          color: 'white',
                          fontSize: '13px',
                          backgroundColor: 'rgba(255,255,255,0.03)',
                          borderRadius: '8px',
                          '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
                          '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                          '&.Mui-focused fieldset': { borderColor: '#a855f7' },
                        },
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleAddKeyword(platform)}
                      sx={{ color: '#a855f7' }}
                    >
                      <Plus size={16} />
                    </IconButton>
                  </Box>
                </Box>
              )
            })}
          </Box>
        )}

        {step === 'saving' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4, gap: 2.5 }}>
            <CircularProgress sx={{ color: '#a855f7' }} size={36} />
            <AnimatedLoadingText
              messages={[
                'Creating your signal',
                'Generating search keywords',
                'Starting initial scan',
              ]}
              interval={2500}
              fontSize="14px"
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {step === 'describe' && (
          <Button
            onClick={() => { handleGenerateKeywords().catch(() => {}) }}
            disabled={!description.trim() || selectedPlatforms.length === 0 || generatingKeywords}
            startIcon={generatingKeywords ? <CircularProgress size={16} /> : <Sparkles size={16} />}
            sx={{
              background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
              color: 'white',
              borderRadius: '10px',
              textTransform: 'none',
              px: 3,
              '&:disabled': { opacity: 0.5, color: 'white' },
            }}
          >
            {generatingKeywords ? 'Generating...' : 'Generate Keywords'}
          </Button>
        )}
        {step === 'keywords' && (
          <>
            <Button
              onClick={() => setStep('describe')}
              sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none' }}
            >
              Back
            </Button>
            <Button
              onClick={() => { handleCreate().catch(() => {}) }}
              disabled={creating}
              sx={{
                background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
                color: 'white',
                borderRadius: '10px',
                textTransform: 'none',
                px: 3,
              }}
            >
              Create Topic
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
